import { Breadcrumb, Grid, Icon, Tooltip } from '@bookora/ui';
import type { PageCrumb } from '@bookora/ui';
import { Link, useLocation } from 'react-router-dom';

import { NAV_GROUPS, canOpenSection, findSectionByPath } from '@/app/sections';
import { usePermissions } from '@/permissions/use-can';

/**
 * Paths that live under a section's URL but are NOT that section's screen, so
 * the section crumb would be a lie. `/settings/security` is the account's own
 * security page, open to every role — a منشی on it saw «مدیریت › تنظیمات»,
 * clicked «تنظیمات», and got «دسترسی ندارید».
 */
const STANDALONE_PAGES: Record<string, string> = {
  '/settings/security': 'امنیت حساب',
};

/**
 * Standalone pages that own a whole path prefix. «ثبت اطلاعات از روی عکس» lives
 * under `/import/photo` (and `/import/photo/:jobId`) and belongs to no sidebar
 * section — it is reached from the patients, accounting, procedures, inventory
 * and calendar screens alike.
 */
const STANDALONE_PREFIXES: readonly (readonly [string, string])[] = [
  ['/import/photo', 'ثبت اطلاعات از روی عکس'],
];

function standaloneTitle(pathname: string): string | undefined {
  const exact = STANDALONE_PAGES[pathname];
  if (exact) return exact;
  const prefixed = STANDALONE_PREFIXES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  return prefixed?.[1];
}

/**
 * The trail's first crumb: the dashboard, drawn as a house. An icon-only link
 * is named for a screen reader (`aria-label`) and, with the kit's label
 * tooltip, for everyone else — a sighted user otherwise has to guess where
 * the house goes.
 */
function HomeCrumb() {
  return (
    <Tooltip kind="label" title="داشبورد">
      <Link to="/" aria-label="داشبورد">
        <Icon name="home" />
      </Link>
    </Tooltip>
  );
}

/**
 * The trail, derived from the section registry rather than from the URL text.
 *
 * A path segment becomes a crumb only when it names a KNOWN section — an opaque
 * id like `/patients/clx9k2m0a…` is not something to show a user. Detail pages
 * know the record's name, so they pass `<PageHeader crumbs>`; the shell
 * provides `PageHeader.CrumbSlot`, which routes those crumbs HERE instead of
 * letting the header draw a second trail under this one. `pageCrumbs` then
 * replaces the section crumb — pages start their trail at their section.
 */
export function AppBreadcrumbs({ pageCrumbs }: { pageCrumbs?: PageCrumb[] | null }) {
  const location = useLocation();
  const permissions = usePermissions();
  const screens = Grid.useBreakpoint();
  const section = findSectionByPath(location.pathname);
  const standalone = standaloneTitle(location.pathname);

  // The dashboard is the root — a one-item trail pointing at the page you are
  // already on is noise.
  if (!standalone && (!section || section.path === '/')) return null;

  if (standalone) {
    return (
      <Breadcrumb
        style={{ marginBlockEnd: 'var(--bk-space-4)' }}
        items={[{ title: <HomeCrumb /> }, { title: standalone }]}
      />
    );
  }

  if (!section) return null;

  // The group heading is the sidebar's filing label, not a place: it cannot be
  // clicked, and on a phone it pushed the crumbs that CAN be onto two lines.
  const group =
    screens.md === false ? undefined : NAV_GROUPS.find((entry) => entry.key === section.group);
  const isSectionRoot = location.pathname === section.path;
  // Linked only when the caller can open it; otherwise it is plain text. A
  // crumb that leads to a 403 is a trap, not a trail.
  const sectionReachable = canOpenSection(section, permissions);

  return (
    <Breadcrumb
      style={{ marginBlockEnd: 'var(--bk-space-4)' }}
      items={[
        { title: <HomeCrumb /> },
        ...(group ? [{ title: group.label }] : []),
        ...(pageCrumbs && pageCrumbs.length > 0
          ? pageCrumbs.map((crumb, index) => ({
              title:
                crumb.to && index < pageCrumbs.length - 1 ? (
                  <Link to={crumb.to}>{crumb.label}</Link>
                ) : (
                  crumb.label
                ),
            }))
          : [
              {
                title:
                  isSectionRoot || !sectionReachable ? (
                    section.label
                  ) : (
                    <Link to={section.path}>{section.label}</Link>
                  ),
              },
            ]),
      ]}
    />
  );
}
